// React-Query hooks для auth-эндпойнтов (Sprint 1 §3.3).
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §8.1 + stage-4-screens.md §2.
//
// КЛЮЧЕВЫЕ КОНТРАКТЫ (см. §2.2 stage-4):
//   - useLoginMutation чистый: НЕ invalidate'ит queries в onSuccess. Race protection
//     (см. §2.2 «Race protection (login)»): screen-handler сам делает await setToken()
//     ДО invalidate(profileMe), иначе refetch уйдёт без Bearer → 401 → loop.
//   - useLogoutMutation тоже не трогает кэш: screen вызывает queryClient.clear()
//     ДО clearToken (порядок важен — иначе refetch уйдёт со старым токеном).
//   - useLoginMutation подмешивает device_name через deviceName() сам;
//     компоненты передают только {email, password} (тип LoginFormRequest, см. types.ts).

import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { deviceName } from '@/lib/platform';
import { currentPushToken, forgetPushToken } from '@/lib/push';
import type {
  ApiError,
  AuthMeResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginFormRequest,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  RegisterRequest,
  RegisterResponse,
  ResendCodeRequest,
  ResendCodeResponse,
  ResetPasswordFormRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyCodeFormRequest,
  VerifyCodeRequest,
  VerifyResetCodeRequest,
  VerifyResetCodeResponse,
} from '@/api/types';

export function useRegisterMutation(): UseMutationResult<RegisterResponse, AxiosError<ApiError>, RegisterRequest> {
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post<RegisterResponse>('/auth/register', body);
      return data;
    },
  });
}

export function useLoginMutation(): UseMutationResult<LoginResponse, AxiosError<ApiError>, LoginFormRequest> {
  return useMutation({
    mutationFn: async (form) => {
      const body: LoginRequest = { ...form, device_name: deviceName() };
      const { data } = await api.post<LoginResponse>('/auth/login', body);
      return data;
    },
  });
}

// OTP verify-code (docs/specs/auth-otp-verification §4.2). Как login: чистый,
// НЕ invalidate'ит queries — экран сам делает await setToken() ДО invalidate
// (race protection, см. §2.2). Подмешивает device_name через deviceName().
export function useVerifyCodeMutation(): UseMutationResult<LoginResponse, AxiosError<ApiError>, VerifyCodeFormRequest> {
  return useMutation({
    mutationFn: async (form) => {
      const body: VerifyCodeRequest = { ...form, device_name: deviceName() };
      const { data } = await api.post<LoginResponse>('/auth/verify-code', body);
      return data;
    },
  });
}

// resend-code идемпотентен (anti-enumeration, всегда generic 200). signup —
// без challenge; signin — с verificationChallenge (D5).
export function useResendCodeMutation(): UseMutationResult<ResendCodeResponse, AxiosError<ApiError>, ResendCodeRequest> {
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post<ResendCodeResponse>('/auth/resend-code', body);
      return data;
    },
  });
}

export function useLogoutMutation(): UseMutationResult<LogoutResponse, AxiosError<ApiError>, void> {
  return useMutation({
    mutationFn: async () => {
      // HEAL-46b §8.3 (TC-ERR-008) — токен устройства отзывается тем же запросом,
      // что и выход. Отдельным вызовом после выхода его отозвать уже нельзя:
      // сессионный токен к тому моменту удалён, и запрос получит 401.
      const pushToken = await currentPushToken();

      const { data } = await api.post<LogoutResponse>(
        '/auth/logout',
        pushToken ? { push_token: pushToken } : {},
      );

      await forgetPushToken();

      return data;
    },
  });
}

export function useForgotPasswordMutation(): UseMutationResult<ForgotPasswordResponse, AxiosError<ApiError>, ForgotPasswordRequest> {
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post<ForgotPasswordResponse>('/auth/forgot-password', body);
      return data;
    },
  });
}

// reset-password отдаёт LoginResponse (автологин). device_name подмешивает hook.
export function useResetPasswordMutation(): UseMutationResult<ResetPasswordResponse, AxiosError<ApiError>, ResetPasswordFormRequest> {
  return useMutation({
    mutationFn: async (form) => {
      const body: ResetPasswordRequest = { ...form, device_name: deviceName() };
      const { data } = await api.post<ResetPasswordResponse>('/auth/reset-password', body);
      return data;
    },
  });
}

// OTP reset: verify-reset-code → reset_token (broker-токен) для reset-password.
export function useVerifyResetCodeMutation(): UseMutationResult<VerifyResetCodeResponse, AxiosError<ApiError>, VerifyResetCodeRequest> {
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.post<VerifyResetCodeResponse>('/auth/verify-reset-code', body);
      return data;
    },
  });
}

export function useAuthMe(enabled = true): UseQueryResult<AuthMeResponse, AxiosError<ApiError>> {
  return useQuery({
    queryKey: queryKeys.authMe,
    queryFn: async () => {
      const { data } = await api.get<AuthMeResponse>('/auth/me');
      return data;
    },
    enabled,
  });
}
